import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/navbar.css';
import { getAdminProfile } from '../globals/Services';

export default function Navbar({ toggleSidebar }) {
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [isLoading, setIsLoading] = useState(false);
  
  const notificationRef = useRef(null);
  const settingsRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const settingsDropdownRef = useRef(null);
  const profileModalRef = useRef(null);
  const navigate = useNavigate();

  // Calculate dropdown position
  const calculateDropdownPosition = (iconRef) => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      return {
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right
      };
    }
    return { top: 60, right: 0 };
  };

  const toggleNotification = () => {
    if (!showNotificationDropdown) {
      const position = calculateDropdownPosition(notificationRef);
      setDropdownPosition(position);
    }
    setShowNotificationDropdown(!showNotificationDropdown);
    setShowSettingsDropdown(false);
  };

  const toggleSettings = () => {
    if (!showSettingsDropdown) {
      const position = calculateDropdownPosition(settingsRef);
      setDropdownPosition(position);
    }
    setShowSettingsDropdown(!showSettingsDropdown);
    setShowNotificationDropdown(false);
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside notification icon and dropdown
      if (notificationRef.current && !notificationRef.current.contains(event.target) &&
          notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setShowNotificationDropdown(false);
      }
      // Check if click is outside settings icon and dropdown
      if (settingsRef.current && !settingsRef.current.contains(event.target) &&
          settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    };

    const handleResize = () => {
      if (showNotificationDropdown) {
        const position = calculateDropdownPosition(notificationRef);
        setDropdownPosition(position);
      } else if (showSettingsDropdown) {
        const position = calculateDropdownPosition(settingsRef);
        setDropdownPosition(position);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (profileModalOpen) {
          closeProfileModal();
        } else {
          setShowNotificationDropdown(false);
          setShowSettingsDropdown(false);
        }
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('resize', handleResize);
    };
  }, [showNotificationDropdown, showSettingsDropdown, profileModalOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (profileModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [profileModalOpen]);

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('loginTime');
    localStorage.removeItem('sessionToken');
    navigate('/login');
  };

  const handleProfileClick = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('sessionToken');
      if (!token) {
        alert('Session expired. Please login again.');
        navigate('/login');
        return;
      }

      const response = await axios.get(getAdminProfile, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Find the admin profile (assuming only one admin)
      const adminProfile = Array.isArray(response.data)
        ? response.data.find(user => user.roles === 'ADMIN')
        : null;
        
      if (adminProfile) {
        setProfileData(adminProfile);
        setProfileModalOpen(true);
      } else {
        alert('Admin profile not found.');
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
        navigate('/login');
      } else {
        alert('Failed to fetch profile. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
    setShowSettingsDropdown(false);
  };

  const closeProfileModal = () => {
    setProfileModalOpen(false);
    setProfileData(null);
  };



  return (
    <nav className="navbar">
      <div className="navbar-left">
        <button
          className="mobile-hamburger"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          ☰
        </button>
      </div>

      <div className="navbar-right">
        {/* Notification Icon */}
        <div 
          className="icon-wrapper" 
          onClick={toggleNotification}
          ref={notificationRef}
          title="Notifications"
        >
          🔔
        </div>

        {/* Settings Icon */}
        <div 
          className="icon-wrapper" 
          onClick={toggleSettings}
          ref={settingsRef}
          title="Settings"
        >
          ⚙️
        </div>
      </div>

      {/* Notification Dropdown */}
      {showNotificationDropdown && (
        <div 
          ref={notificationDropdownRef}
          className="dropdown-fixed"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            backgroundColor: '#374151',
            border: '1px solid #4b5563',
            color: '#fff',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
            borderRadius: '6px',
            minWidth: '200px',
            padding: '16px',
            zIndex: 200
          }}
        >
          <div style={{ textAlign: 'center', color: '#9ca3af' }}>
            <p style={{ margin: 0, fontSize: '14px' }}>No new notifications</p>
          </div>
        </div>
      )}

      {/* Settings Dropdown */}
      {showSettingsDropdown && (
        <div 
          ref={settingsDropdownRef}
          className="dropdown-fixed"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            backgroundColor: '#374151',
            border: '1px solid #4b5563',
            color: '#fff',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
            borderRadius: '6px',
            minWidth: '180px',
            padding: '8px',
            zIndex: 200
          }}
        >
          <div 
            className="dropdown-item" 
            onClick={handleProfileClick}
            style={{ opacity: isLoading ? 0.6 : 1, pointerEvents: isLoading ? 'none' : 'auto' }}
          >
            👤 {isLoading ? 'Loading...' : 'Profile'}
          </div>
          <div className="dropdown-item">🎨 Theme Settings</div>
          <div className="dropdown-item" onClick={handleLogout}>
            🚪 Logout
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {profileModalOpen && profileData && (
        <div 
          className="profile-modal-overlay" 
          onClick={closeProfileModal}
          ref={profileModalRef}
        >
          <div 
            className="profile-modal" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button is handled by CSS ::before pseudo-element */}
            <div 
              style={{
                position: 'absolute',
                top: '12px',
                right: '16px',
                cursor: 'pointer',
                fontSize: '28px',
                color: '#6b7280',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s',
                zIndex: 1
              }}
              onClick={closeProfileModal}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }}
            >
              ×
            </div>
            
            <h2>Admin Profile</h2>
            
            <div style={{ marginBottom: '24px' }}>
              <p>
                <strong>Name:</strong> 
                <span>{profileData.firstName} {profileData.lastName}</span>
              </p>
              <p>
                <strong>Email:</strong> 
                <span>{profileData.email}</span>
              </p>
              <p>
                <strong>Role:</strong> 
                <span style={{ 
                  backgroundColor: '#3b82f6', 
                  color: 'white', 
                  padding: '4px 8px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {profileData.roles}
                </span>
              </p>
              <p>
                <strong>Mobile:</strong> 
                <span>{profileData.phoneNo || 'Not provided'}</span>
              </p>
              {profileData.department && (
                <p>
                  <strong>Department:</strong> 
                  <span>{profileData.department}</span>
                </p>
              )}
              {profileData.joinDate && (
                <p>
                  <strong>Join Date:</strong> 
                  <span>{new Date(profileData.joinDate).toLocaleDateString()}</span>
                </p>
              )}
            </div>
            
            <button onClick={closeProfileModal}>
              Close Profile
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}